import { Injectable, Renderer2 } from "@angular/core";

import { filter, map, mergeMap, tap } from "rxjs";
import { TranslateService } from "@ngx-translate/core";

import { BusinessService } from "./business.service";
import { isIE } from "@app/shared/helpers/browser-detection.util";
import { IParamsForReceiptRequest, ReceiptParamsFormat, ReceiptParamsType } from "./business-history/business-requested-history/business-requested-history.models";

@Injectable({
    providedIn: 'root'
})
export class BusinessDownloadService {

    constructor(
        private businessService: BusinessService,
        private translateService: TranslateService,
    ) { }

    /**
     * Prepares the parameters for requesting a registered mail receipt and calls the service to fetch it.
     * 
     * @param messageIdentifier - The unique identifier for the registered mail message.
     * @returns An Observable containing the receipt data.
     */
    requestForReceiptData(messageIdentifier: string) {
        const params: IParamsForReceiptRequest = {
            messageIdentifier: messageIdentifier,
            responseType: ReceiptParamsType.PDF,
            responseFormat: ReceiptParamsFormat.SHORT
        }

        return this.businessService.getRegisteredMailReceipt(params);
    }


    /**
     * Downloads a Base64-encoded file by creating a temporary anchor element and triggering a download.
     * 
     * @param base64File - The Base64-encoded string representing the file content.
     * @param renderer - The Angular Renderer2 instance used for DOM manipulation.
     */
    downloadReceipt(base64File: string, renderer: Renderer2) { //downloadBase64File
        const link = renderer.createElement('a');
        renderer.setAttribute(link, 'href', `data:application/pdf;base64,${base64File}`);
        renderer.setAttribute(link, 'download', this.translateService.instant('businessTexts.receipt')); // Set the file name
        renderer.setStyle(link, 'display', 'none');

        // Append the link to the body, trigger the click, and remove it
        renderer.appendChild(document.body, link);
        link.click();
        renderer.removeChild(document.body, link);
    }


    /**
     * Fetches files associated with a package by retrieving document IDs and fetching each file.
     * 
     * @param parcelDocIds - The IDs of the documents in the package to be fetched.
     * @returns An Observable that emits the fetched files (as responses).
     */
    fetchPackageFiles(parcelDocIds) {
        return this.businessService.getDownloadDocIds(parcelDocIds)
            .pipe(
                filter(res => !!res),                                     // Filter out falsy responses
                map(res => res.data.documentsForDownload as string[]),    // Extract document IDs
                mergeMap(ids => ids),                                     // Flat map over the IDs
                mergeMap(id => this.businessService.getRequestedFile(id)), // Fetch each file
            )
    }


    /**
     * Downloads a file to the client's PC.
     * 
     * @param responseData - The HTTP response containing the file data and headers.
     */
    downloadBlobFileToClientPC(responseData) {
        const signedFileName = responseData.headers.get('Content-Disposition');
        let fileName = signedFileName.substring(signedFileName.indexOf('=') + 2, (signedFileName.length - 1));

        if (isIE()) {
            fileName = decodeURIComponent(fileName);
            const nav = (window.navigator as any)
            if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(responseData.body, fileName);
            }
        } else {
            const url = window.URL.createObjectURL(responseData.body);
            const link = document.createElement('a');
            link.href = url;
            link.download = decodeURI(fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

}